/**
 * Benepass transfer reporting — overall summary by status and breakdown by partner product.
 */

import React, { useEffect, useMemo, useState } from 'react';

import { Badge } from '@/components/common/Badge';
import { Card, CardBody, CardHeader, StatCard } from '@/components/common/Card';
import type { Column } from '@/components/common/Table';
import { Table } from '@/components/common/Table';
import cardStyles from '@/components/common/Card/Card.module.scss';
import type {
  PartnerProductBreakdownItem,
  PartnerProductBreakdownResponse,
  TransferStatusCount,
  TransferSummaryResponse,
} from '@/app/services/benepassTransferService';
import {
  fetchPartnerProductBreakdown,
  fetchTransferSummary,
} from '@/app/services/benepassTransferService';

import styles from './BenepassTransferSummary.module.scss';
import { transferStatusBadgeVariant } from '@/pages/BenepassTransfers/transferStatusBadgeVariant';

function formatMoney(amount: string): string {
  const n = Number.parseFloat(amount);
  if (!Number.isFinite(n)) return amount;
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(n);
}

export const BenepassTransferSummary: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<TransferSummaryResponse | null>(null);
  const [partnerBreakdown, setPartnerBreakdown] = useState<PartnerProductBreakdownResponse | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const [sum, partner] = await Promise.all([
          fetchTransferSummary(),
          fetchPartnerProductBreakdown(),
        ]);
        if (!cancelled) {
          setSummary(sum);
          setPartnerBreakdown(partner);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          const message = e instanceof Error ? e.message : 'Unknown error';
          setError(message);
          setSummary(null);
          setPartnerBreakdown(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const statusColumns = useMemo((): Column<TransferStatusCount>[] => {
    return [
      {
        key: 'status',
        header: 'Status',
        render: (row) => (
          <Badge variant={transferStatusBadgeVariant(row.status)} size="sm">
            {row.status}
          </Badge>
        ),
      },
      {
        key: 'count',
        header: 'Count',
        align: 'right',
      },
      {
        key: 'total_amount',
        header: 'Amount',
        align: 'right',
        render: (row) => formatMoney(row.total_amount),
      },
    ];
  }, []);

  const partnerColumns = useMemo((): Column<PartnerProductBreakdownItem>[] => {
    return [
      {
        key: 'partner_name',
        header: 'Partner',
        render: (row) => row.partner_name ?? '—',
      },
      {
        key: 'doing_business_as',
        header: 'Doing business as',
        render: (row) => row.doing_business_as ?? '—',
      },
      {
        key: 'partner_product_id',
        header: 'Product ID',
        render: (row) => (row.partner_product_id != null ? String(row.partner_product_id) : '—'),
      },
      {
        key: 'totals',
        header: 'Totals',
        render: (row) => `${row.total_count} · ${formatMoney(row.total_amount)}`,
      },
      {
        key: 'counts',
        header: 'Per status',
        render: (row) => (
          <ul className={styles.nestedCounts}>
            {row.counts.map((c) => (
              <li key={c.status}>
                <Badge variant={transferStatusBadgeVariant(c.status)} size="sm">
                  {c.status}
                </Badge>{' '}
                {c.count} ({formatMoney(c.total_amount)})
              </li>
            ))}
          </ul>
        ),
      },
    ];
  }, []);

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>Benepass transfers</h1>
          <p className={styles.pageDescription}>
            Migration status from Benepass to InComm cards — totals across all transfers and per
            partner product.
          </p>
        </div>
        <a className={styles.toolbarLink} href="#/benepass-transfers/list">
          View all transfers
        </a>
      </header>

      {loading && (
        <div className={styles.loading}>Loading Benepass reports…</div>
      )}

      {error != null && (
        <div className={styles.errorMessage} role="alert">
          Failed to load Benepass reports: {error}
        </div>
      )}

      {!loading && error == null && summary != null && (
        <>
          <div className={styles.statsGrid}>
            <div data-testid="benepass-total-count" className={styles.statWrap}>
              <StatCard label="Total transfers" value={summary.total_count} />
            </div>
            <div className={cardStyles.statCard} data-testid="benepass-total-amount">
              <div className={cardStyles.statLabel}>Total reward amount</div>
              <div className={cardStyles.statValue}>{formatMoney(summary.total_amount)}</div>
            </div>
          </div>

          <div className={styles.sectionStack}>
            <Card>
              <CardHeader title="By status" subtitle="Counts and reward amounts per transfer status" />
              <CardBody>
                {summary.counts.length === 0 ? (
                  <p className={styles.pageDescription}>No transfers in scope.</p>
                ) : (
                  <Table
                    columns={statusColumns}
                    data={summary.counts}
                    keyExtractor={(row) => row.status}
                    emptyMessage="No status rows"
                  />
                )}
              </CardBody>
            </Card>

            {partnerBreakdown != null && partnerBreakdown.items.length > 0 && (
              <Card>
                <CardHeader
                  title="By partner product"
                  subtitle="Grouped using eligible participant linkage to partner configuration"
                />
                <CardBody>
                  <div data-testid="benepass-partner-breakdown">
                    <Table
                      columns={partnerColumns}
                      data={partnerBreakdown.items}
                      keyExtractor={(row) =>
                        `${String(row.partner_product_id ?? 'null')}-${row.partner_name ?? ''}-${row.doing_business_as ?? ''}`
                      }
                      emptyMessage="No partner rows"
                    />
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BenepassTransferSummary;
